import { distanceMeters } from '../components/map/geo';
import { horodatagePoint } from './traceGps';

/**
 * Lecture d'un trajet complet: où le camion s'est arrêté, et à quelle vitesse
 * il roulait sur chaque tronçon. Fonctions pures, sans carte ni DOM.
 */

/** En dessous (inclus), le camion est à l'arrêt. */
export const VITESSE_ARRET_KMH = 3;
/** Au-delà (exclu), le camion roule vite. */
export const VITESSE_RAPIDE_KMH = 80;
/** Au-delà (exclu), la vitesse est excessive. */
export const VITESSE_EXCES_KMH = 90;
/** Un arrêt se numérote à partir de 10 minutes. */
export const ARRET_DUREE_MIN_SECONDES = 600;
/** Le camion reste dans ce rayon, autour de son point d'arrêt. */
export const ARRET_RAYON_METRES = 150;

const nombreOuNull = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
};

const coordonneesValides = (p) =>
  Boolean(p) && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude));

/**
 * Les arrêts d'une trace: des relevés consécutifs à l'arrêt (vitesse sous le
 * seuil, ou inconnue mais sans que le camion se soit éloigné), tous dans un
 * petit rayon autour du premier, pendant au moins `dureeMinS` secondes.
 *
 * Rend `[{debut, fin, dureeS, latitude, longitude, index, indexFin}]`, du
 * premier au dernier; `debut` et `fin` sont les horodatages d'origine.
 */
export function detecterArrets(
  points,
  { vitesseMax = VITESSE_ARRET_KMH, rayonM = ARRET_RAYON_METRES, dureeMinS = ARRET_DUREE_MIN_SECONDES } = {},
) {
  const tries = (Array.isArray(points) ? points : [])
    .filter(coordonneesValides)
    .map((point, rang) => ({ point, rang, t: horodatagePoint(point) }))
    .filter((e) => !Number.isNaN(e.t))
    .sort((a, b) => a.t - b.t || a.rang - b.rang)
    .map((e) => ({ ...e, vitesse: nombreOuNull(e.point.speed_kmh) }));

  const arrets = [];
  let i = 0;
  while (i < tries.length) {
    const premier = tries[i];
    if (premier.vitesse === null || premier.vitesse > vitesseMax) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (
      j < tries.length &&
      (tries[j].vitesse === null || tries[j].vitesse <= vitesseMax) &&
      distanceMeters(premier.point, tries[j].point) <= rayonM
    ) {
      j += 1;
    }
    const dernier = tries[j - 1];
    const dureeS = (dernier.t - premier.t) / 1000;
    if (j - i >= 2 && dureeS >= dureeMinS) {
      const groupe = tries.slice(i, j);
      const somme = groupe.reduce(
        (acc, e) => ({ lat: acc.lat + Number(e.point.latitude), lng: acc.lng + Number(e.point.longitude) }),
        { lat: 0, lng: 0 },
      );
      arrets.push({
        debut: premier.point.timestamp,
        fin: dernier.point.timestamp,
        dureeS,
        latitude: somme.lat / groupe.length,
        longitude: somme.lng / groupe.length,
        index: i,
        indexFin: j - 1,
      });
      i = j;
    } else {
      i += 1;
    }
  }
  return arrets;
}

/** La classe de vitesse d'un relevé: 'arret', 'normal', 'rapide' ou 'exces'. */
export function classeVitesse(vitesse) {
  const v = nombreOuNull(vitesse);
  if (v === null) return 'normal';
  if (v <= VITESSE_ARRET_KMH) return 'arret';
  if (v <= VITESSE_RAPIDE_KMH) return 'normal';
  if (v <= VITESSE_EXCES_KMH) return 'rapide';
  return 'exces';
}

/**
 * Découpe une trace (du plus ancien au plus récent) en tronçons d'une même
 * classe de vitesse (celle du relevé de départ de chaque petit trajet). Deux tronçons voisins partagent leur point de jonction:
 * la ligne reste continue. `debut` et `fin` sont les rangs, dans `points`,
 * du premier et du dernier relevé du tronçon.
 */
export function segmentsParVitesse(points) {
  const valides = [];
  (Array.isArray(points) ? points : []).forEach((point, rang) => {
    if (coordonneesValides(point)) valides.push({ point, rang });
  });
  const segments = [];
  let courant = null;
  // Chaque petit trajet entre deux relevés prend la classe du relevé de départ.
  for (let k = 0; k < valides.length - 1; k += 1) {
    const depart = valides[k];
    const arrivee = valides[k + 1];
    const classe = classeVitesse(depart.point.speed_kmh);
    const positionArrivee = [Number(arrivee.point.latitude), Number(arrivee.point.longitude)];
    if (courant && courant.classe === classe) {
      courant.positions.push(positionArrivee);
      courant.fin = arrivee.rang;
    } else {
      if (courant) segments.push(courant);
      courant = {
        classe,
        positions: [[Number(depart.point.latitude), Number(depart.point.longitude)], positionArrivee],
        debut: depart.rang,
        fin: arrivee.rang,
      };
    }
  }
  if (courant) segments.push(courant);
  return segments;
}

/**
 * La partie des tronçons déjà parcourue quand le camion est au relevé
 * `index`: les tronçons finis, et le tronçon en cours coupé au camion.
 */
export function segmentsJusqua(segments, index) {
  const sortie = [];
  (segments || []).forEach((segment) => {
    if (segment.debut > index) return;
    if (segment.fin <= index) {
      sortie.push(segment);
      return;
    }
    sortie.push({ ...segment, positions: segment.positions.slice(0, index - segment.debut + 1) });
  });
  return sortie.filter((s) => s.positions.length >= 2);
}
